import React, { useState, useEffect } from 'react';
import { ref, onValue, update, remove } from 'firebase/database';
import { db } from '../../firebase/config';
import { FaCheckCircle, FaTimesCircle, FaEye, FaTimes, FaTrash } from 'react-icons/fa';

const OrderNotPlacedManager = () => {
    const [orders, setOrders] = useState([]);
    const [filter, setFilter] = useState('all'); // all, pending, verified, rejected
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [notification, setNotification] = useState({ show: false, message: '', type: '' });

    useEffect(() => {
        const ordersRef = ref(db, 'HTAMS/orderNotPlaced');
        const unsubscribe = onValue(ordersRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const ordersArray = Object.entries(data).map(([id, order]) => ({
                    id,
                    ...order
                }));
                // Sort by timestamp (newest first)
                ordersArray.sort((a, b) => b.timestamp - a.timestamp);
                setOrders(ordersArray);
            } else {
                setOrders([]);
            }
        });
        return () => unsubscribe();
    }, []);

    const showNotification = (message, type = 'info') => {
        setNotification({ show: true, message, type });
        setTimeout(() => {
            setNotification({ show: false, message: '', type: '' });
        }, 5000);
    };

    const updateOrderStatus = async (orderId, status) => {
        setLoading(true);
        try {
            const orderRef = ref(db, `HTAMS/orderNotPlaced/${orderId}`);
            await update(orderRef, {
                status,
                verifiedAt: new Date().toISOString()
            });
            showNotification(`Order ${status.toLowerCase()} successfully`, 'success');
            setShowModal(false);
            setSelectedOrder(null);
        } catch (error) {
            console.error('Error updating order:', error);
            showNotification('Failed to update order status', 'error');
        } finally {
            setLoading(false);
        }
    };

    const deleteOrder = async (orderId) => {
        if (!window.confirm('Are you sure you want to delete this order submission?')) return;

        setLoading(true);
        try {
            const orderRef = ref(db, `HTAMS/orderNotPlaced/${orderId}`);
            await remove(orderRef);
            showNotification('Order deleted successfully', 'success');
            setShowModal(false);
            setSelectedOrder(null);
        } catch (error) {
            console.error('Error deleting order:', error);
            showNotification('Failed to delete order', 'error');
        } finally {
            setLoading(false);
        }
    };

    const filteredOrders = orders.filter(order => {
        if (filter === 'all') return true;
        if (filter === 'pending') return order.status === 'Pending Verification';
        if (filter === 'verified') return order.status === 'Verified';
        if (filter === 'rejected') return order.status === 'Rejected';
        return true;
    });

    const getStatusColor = (status) => {
        switch (status) {
            case 'Pending Verification':
                return '#ff9800';
            case 'Verified':
                return '#4caf50';
            case 'Rejected':
                return '#f44336';
            default:
                return '#9e9e9e';
        }
    };

    return (
        <div className="onp-manager-container">
            <style>{`
                .onp-manager-container {
                    padding: 20px;
                    max-width: 1400px;
                    margin: 0 auto;
                }

                .onp-manager-header {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                    padding: 30px;
                    border-radius: 12px;
                    margin-bottom: 30px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .onp-manager-header h1 {
                    margin: 0;
                    font-size: 2rem;
                }

                .filter-tabs {
                    display: flex;
                    gap: 10px;
                    margin-bottom: 20px;
                    flex-wrap: wrap;
                }

                .filter-tab {
                    padding: 12px 24px;
                    border: 2px solid #e0e0e0;
                    background: white;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: all 0.3s;
                    font-weight: 600;
                }

                .filter-tab.active {
                    background: #667eea;
                    color: white;
                    border-color: #667eea;
                }

                .filter-tab:hover {
                    border-color: #667eea;
                }

                .orders-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
                    gap: 20px;
                }

                .order-card {
                    background: white;
                    border-radius: 12px;
                    padding: 20px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                    transition: all 0.3s;
                    border-left: 4px solid;
                }

                .order-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 6px 16px rgba(0,0,0,0.15);
                }

                .order-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 15px;
                }

                .order-id {
                    font-weight: 600;
                    color: #333;
                    font-size: 0.9rem;
                }

                .order-status {
                    padding: 6px 12px;
                    border-radius: 20px;
                    font-size: 0.85rem;
                    font-weight: 600;
                    color: white;
                }

                .order-info {
                    margin-bottom: 15px;
                }

                .info-row {
                    display: flex;
                    justify-content: space-between;
                    margin-bottom: 8px;
                    font-size: 0.95rem;
                }

                .info-label {
                    color: #666;
                }

                .info-value {
                    font-weight: 600;
                    color: #333;
                }

                .order-actions {
                    display: flex;
                    gap: 10px;
                    margin-top: 15px;
                }

                .action-btn {
                    flex: 1;
                    padding: 10px;
                    border: none;
                    border-radius: 6px;
                    cursor: pointer;
                    font-weight: 600;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                    transition: all 0.3s;
                }

                .view-btn {
                    background: #2196f3;
                    color: white;
                }

                .view-btn:hover {
                    background: #1976d2;
                }

                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0,0,0,0.7);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 10000;
                    padding: 20px;
                }

                .modal-content {
                    background: white;
                    border-radius: 12px;
                    max-width: 900px;
                    width: 100%;
                    max-height: 90vh;
                    overflow-y: auto;
                    position: relative;
                }

                .modal-header {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                    padding: 20px;
                    border-radius: 12px 12px 0 0;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .modal-close {
                    background: rgba(255,255,255,0.2);
                    border: none;
                    color: white;
                    width: 36px;
                    height: 36px;
                    border-radius: 50%;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.2rem;
                }

                .modal-body {
                    padding: 30px;
                }

                .detail-section {
                    margin-bottom: 30px;
                }

                .section-title {
                    font-size: 1.2rem;
                    font-weight: 600;
                    margin-bottom: 15px;
                    color: #333;
                    border-bottom: 2px solid #667eea;
                    padding-bottom: 8px;
                }

                .detail-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 15px;
                }

                .detail-item {
                    padding: 12px;
                    background: #f5f5f5;
                    border-radius: 8px;
                }

                .detail-label {
                    font-size: 0.85rem;
                    color: #666;
                    margin-bottom: 4px;
                }

                .detail-value {
                    font-weight: 600;
                    color: #333;
                }

                .payment-screenshot {
                    max-width: 100%;
                    border-radius: 8px;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                    cursor: pointer;
                }

                .payment-screenshot:hover {
                    transform: scale(1.02);
                    transition: transform 0.3s;
                }

                .products-list {
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                }

                .product-item {
                    padding: 12px;
                    background: #f5f5f5;
                    border-radius: 8px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .modal-actions {
                    display: flex;
                    gap: 15px;
                    padding: 20px 30px;
                    background: #f9fafb;
                    border-radius: 0 0 12px 12px;
                }

                .verify-btn {
                    background: #4caf50;
                    color: white;
                }

                .verify-btn:hover {
                    background: #45a049;
                }

                .reject-btn {
                    background: #f44336;
                    color: white;
                }

                .reject-btn:hover {
                    background: #d32f2f;
                }

                .delete-btn {
                    background: #9e9e9e;
                    color: white;
                }

                .delete-btn:hover {
                    background: #757575;
                }

                .notification {
                    position: fixed;
                    top: 20px;
                    right: 20px;
                    padding: 16px 24px;
                    border-radius: 8px;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.2);
                    z-index: 10001;
                    animation: slideIn 0.3s;
                }

                .notification.success {
                    background: #4caf50;
                    color: white;
                }

                .notification.error {
                    background: #f44336;
                    color: white;
                }

                @keyframes slideIn {
                    from {
                        transform: translateX(400px);
                        opacity: 0;
                    }
                    to {
                        transform: translateX(0);
                        opacity: 1;
                    }
                }

                .empty-state {
                    text-align: center;
                    padding: 60px 20px;
                    color: #999;
                }

                .empty-state-icon {
                    font-size: 4rem;
                    margin-bottom: 20px;
                }

                @media (max-width: 768px) {
                    .orders-grid {
                        grid-template-columns: 1fr;
                    }

                    .modal-content {
                        max-height: 100vh;
                        border-radius: 0;
                    }

                    .detail-grid {
                        grid-template-columns: 1fr;
                    }
                }
            `}</style>

            {/* Notification */}
            {notification.show && (
                <div className={`notification ${notification.type}`}>
                    {notification.message}
                </div>
            )}

            {/* Header */}
            <div className="onp-manager-header">
                <h1>📋 Order Not Placed - Manager</h1>
                <div style={{ fontSize: '1.2rem' }}>
                    Total: {orders.length}
                </div>
            </div>

            {/* Filters */}
            <div className="filter-tabs">
                <button
                    className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
                    onClick={() => setFilter('all')}
                >
                    All ({orders.length})
                </button>
                <button
                    className={`filter-tab ${filter === 'pending' ? 'active' : ''}`}
                    onClick={() => setFilter('pending')}
                >
                    Pending ({orders.filter(o => o.status === 'Pending Verification').length})
                </button>
                <button
                    className={`filter-tab ${filter === 'verified' ? 'active' : ''}`}
                    onClick={() => setFilter('verified')}
                >
                    Verified ({orders.filter(o => o.status === 'Verified').length})
                </button>
                <button
                    className={`filter-tab ${filter === 'rejected' ? 'active' : ''}`}
                    onClick={() => setFilter('rejected')}
                >
                    Rejected ({orders.filter(o => o.status === 'Rejected').length})
                </button>
            </div>

            {/* Orders Grid */}
            {filteredOrders.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-state-icon">📭</div>
                    <h3>No orders found</h3>
                    <p>There are no orders matching your filter</p>
                </div>
            ) : (
                <div className="orders-grid">
                    {filteredOrders.map(order => (
                        <div
                            key={order.id}
                            className="order-card"
                            style={{ borderLeftColor: getStatusColor(order.status) }}
                        >
                            <div className="order-header">
                                <div className="order-id">
                                    {new Date(order.timestamp).toLocaleDateString()}
                                </div>
                                <div
                                    className="order-status"
                                    style={{ backgroundColor: getStatusColor(order.status) }}
                                >
                                    {order.status}
                                </div>
                            </div>

                            <div className="order-info">
                                <div className="info-row">
                                    <span className="info-label">Customer:</span>
                                    <span className="info-value">{order.customerInfo?.name}</span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">Phone:</span>
                                    <span className="info-value">{order.customerInfo?.phone}</span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">Amount:</span>
                                    <span className="info-value">₹{order.orderDetails?.amount}</span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">Payment ID:</span>
                                    <span className="info-value">{order.paymentInfo?.paymentId}</span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">Products:</span>
                                    <span className="info-value">{order.orderDetails?.products?.length || 0}</span>
                                </div>
                            </div>

                            <div className="order-actions">
                                <button
                                    className="action-btn view-btn"
                                    onClick={() => {
                                        setSelectedOrder(order);
                                        setShowModal(true);
                                    }}
                                >
                                    <FaEye /> View Details
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal */}
            {showModal && selectedOrder && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Order Details</h2>
                            <button className="modal-close" onClick={() => setShowModal(false)}>
                                <FaTimes />
                            </button>
                        </div>

                        <div className="modal-body">
                            {/* Customer Info */}
                            <div className="detail-section">
                                <h3 className="section-title">Customer Information</h3>
                                <div className="detail-grid">
                                    <div className="detail-item">
                                        <div className="detail-label">Name</div>
                                        <div className="detail-value">{selectedOrder.customerInfo?.name}</div>
                                    </div>
                                    <div className="detail-item">
                                        <div className="detail-label">Email</div>
                                        <div className="detail-value">{selectedOrder.customerInfo?.email}</div>
                                    </div>
                                    <div className="detail-item">
                                        <div className="detail-label">Phone</div>
                                        <div className="detail-value">{selectedOrder.customerInfo?.phone}</div>
                                    </div>
                                    <div className="detail-item">
                                        <div className="detail-label">City</div>
                                        <div className="detail-value">{selectedOrder.customerInfo?.city || 'N/A'}</div>
                                    </div>
                                    <div className="detail-item">
                                        <div className="detail-label">State</div>
                                        <div className="detail-value">{selectedOrder.customerInfo?.state || 'N/A'}</div>
                                    </div>
                                    <div className="detail-item">
                                        <div className="detail-label">District</div>
                                        <div className="detail-value">{selectedOrder.customerInfo?.district || 'N/A'}</div>
                                    </div>
                                    <div className="detail-item">
                                        <div className="detail-label">Pincode</div>
                                        <div className="detail-value">{selectedOrder.customerInfo?.pincode || 'N/A'}</div>
                                    </div>
                                    {selectedOrder.customerInfo?.birthday && (
                                        <div className="detail-item">
                                            <div className="detail-label">Birthday</div>
                                            <div className="detail-value">{new Date(selectedOrder.customerInfo?.birthday).toLocaleDateString()}</div>
                                        </div>
                                    )}
                                </div>
                                {selectedOrder.customerInfo?.address && (
                                    <div className="detail-item" style={{ marginTop: '15px' }}>
                                        <div className="detail-label">Address</div>
                                        <div className="detail-value">{selectedOrder.customerInfo?.address}</div>
                                    </div>
                                )}
                            </div>

                            {/* Products */}
                            <div className="detail-section">
                                <h3 className="section-title">Products</h3>
                                <div className="products-list">
                                    {selectedOrder.orderDetails?.products?.map((product, index) => (
                                        <div key={index} className="product-item">
                                            <span>{product.name}</span>
                                            <span style={{ fontWeight: 600 }}>₹{product.price}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Payment Info */}
                            <div className="detail-section">
                                <h3 className="section-title">Payment Information</h3>
                                <div className="detail-grid">
                                    <div className="detail-item">
                                        <div className="detail-label">Amount</div>
                                        <div className="detail-value">₹{selectedOrder.orderDetails?.amount}</div>
                                    </div>
                                    <div className="detail-item">
                                        <div className="detail-label">Payment Method</div>
                                        <div className="detail-value">{selectedOrder.paymentInfo?.paymentMethod}</div>
                                    </div>
                                    <div className="detail-item">
                                        <div className="detail-label">Payment ID</div>
                                        <div className="detail-value">{selectedOrder.paymentInfo?.paymentId}</div>
                                    </div>
                                    <div className="detail-item">
                                        <div className="detail-label">Screenshot Size</div>
                                        <div className="detail-value">{selectedOrder.paymentInfo?.screenshotSize}</div>
                                    </div>
                                </div>
                            </div>

                            {/* Payment Screenshot */}
                            <div className="detail-section">
                                <h3 className="section-title">Payment Screenshot</h3>
                                <a href={selectedOrder.paymentInfo?.screenshotUrl} target="_blank" rel="noopener noreferrer">
                                    <img
                                        src={selectedOrder.paymentInfo?.screenshotUrl}
                                        alt="Payment proof"
                                        className="payment-screenshot"
                                    />
                                </a>
                            </div>

                            {/* Notes */}
                            {selectedOrder.notes && (
                                <div className="detail-section">
                                    <h3 className="section-title">Additional Notes</h3>
                                    <div className="detail-item">
                                        <div className="detail-value">{selectedOrder.notes}</div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="modal-actions">
                            {selectedOrder.status === 'Pending Verification' && (
                                <>
                                    <button
                                        className="action-btn verify-btn"
                                        onClick={() => updateOrderStatus(selectedOrder.id, 'Verified')}
                                        disabled={loading}
                                    >
                                        <FaCheckCircle /> Verify Order
                                    </button>
                                    <button
                                        className="action-btn reject-btn"
                                        onClick={() => updateOrderStatus(selectedOrder.id, 'Rejected')}
                                        disabled={loading}
                                    >
                                        <FaTimesCircle /> Reject Order
                                    </button>
                                </>
                            )}
                            <button
                                className="action-btn delete-btn"
                                onClick={() => deleteOrder(selectedOrder.id)}
                                disabled={loading}
                            >
                                <FaTrash /> Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OrderNotPlacedManager;
